import type { ReactNode } from 'react'
import { useState } from 'react'

import {
  ResponsiveDialogBody,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { Button } from '@/components/sui/button'
import { Field, FieldLabel } from '@/components/sui/field'
import { Input } from '@/components/sui/input'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useFormatNumber } from '@/hooks/use-format-number'

import locales from '../locales'
import { RadioOptionCard } from '../radio-option-card'

interface EnvelopesStepProps {
  progressBar: ReactNode
  envelopeOptions: Array<string>
  envelopeOption: string | null
  onSelectOption: (option: string) => void
  /** Raw input value for the "Outro" option; empty until the user types. */
  customEnvelopes: string
  onCustomEnvelopesChange: (value: string) => void
  isValid: boolean
  onBack: () => void
  onNext: () => void
}

export function EnvelopesStep({
  progressBar,
  envelopeOptions,
  envelopeOption,
  onSelectOption,
  customEnvelopes,
  onCustomEnvelopesChange,
  isValid,
  onBack,
  onNext,
}: EnvelopesStepProps) {
  const { t } = useComponentTranslation(locales)
  const { formatNumber } = useFormatNumber()
  const [showNextError, setShowNextError] = useState(false)

  const handleSelect = (option: string) => {
    setShowNextError(false)
    onSelectOption(option)
  }

  const handleCustomChange = (value: string) => {
    setShowNextError(false)
    onCustomEnvelopesChange(value)
  }

  const handleNext = () => {
    if (!isValid) {
      setShowNextError(true)
      return
    }
    onNext()
  }

  return (
    <>
      <ResponsiveDialogHeader>
        <ResponsiveDialogTitle>{t('title')}</ResponsiveDialogTitle>
        <ResponsiveDialogDescription>
          {t('steps.description')}
        </ResponsiveDialogDescription>
      </ResponsiveDialogHeader>

      <ResponsiveDialogBody>
        <div className="space-y-4">
          {progressBar}

          <p className="text-sm font-medium">
            {t('steps.envelopes.questionMonthly')}
          </p>

          <div className="space-y-2">
            {envelopeOptions.map((option) => {
              const isCustom = option === 'custom'
              const isSelected = envelopeOption === option

              return (
                <RadioOptionCard
                  key={option}
                  selected={isSelected}
                  onClick={() => handleSelect(option)}
                >
                  {isCustom ? (
                    <div className="flex-1">
                      <span className="font-medium">
                        {t('steps.envelopes.custom')}
                      </span>
                      {isSelected && (
                        <div className="mt-2">
                          <Field>
                            <FieldLabel
                              htmlFor="plan-finder-custom-envelopes"
                              className="text-muted-foreground text-xs"
                            >
                              {t('steps.envelopes.customLabelMonthly')}
                            </FieldLabel>
                            <Input
                              id="plan-finder-custom-envelopes"
                              type="number"
                              min={1}
                              value={customEnvelopes}
                              onChange={(event) =>
                                handleCustomChange(event.target.value)
                              }
                              // The card itself is the radio; typing must not
                              // re-fire its onClick.
                              onClick={(event) => event.stopPropagation()}
                              className="text-center"
                              autoFocus
                            />
                          </Field>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="font-medium">
                      {t('steps.envelopes.optionMonthly', {
                        amount: formatNumber(Number(option)),
                      })}
                    </span>
                  )}
                </RadioOptionCard>
              )
            })}
          </div>

          {envelopeOption === 'custom' &&
            ((customEnvelopes !== '' && Number(customEnvelopes) <= 0) ||
              (showNextError && customEnvelopes === '')) && (
              <p role="alert" className="text-destructive text-xs">
                {t('steps.envelopes.customError')}
              </p>
            )}
          {showNextError && !envelopeOption && (
            <p role="alert" className="text-destructive text-xs font-medium">
              {t('steps.envelopes.selectError')}
            </p>
          )}
        </div>
      </ResponsiveDialogBody>

      <ResponsiveDialogFooter>
        <Button variant="outline" onClick={onBack}>
          {t('actions.back')}
        </Button>
        <Button onClick={handleNext}>{t('actions.next')}</Button>
      </ResponsiveDialogFooter>
    </>
  )
}
